import React from 'react';
import styled from 'styled-components';
import { AgencyXLogo } from 'images/shared';
import { useWindowSize } from 'hooks';
import { motion } from 'framer-motion';
import { Link, useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';

const StartYourJourney = ({ pinkSpray, mesh, threeBalls }) => {
  const { windowWidth } = useWindowSize();

  const { blueAndBlackSpray } = useStaticQuery(graphql`
    query {
      blueAndBlackSpray: file(relativePath: { eq: "omnichannel/blue-and-black-spray.png" }) {
        childImageSharp {
          gatsbyImageData(width: 235)
        }
      }
    }
  `);

  return (
    <Container
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}>
      {windowWidth > 768 && (
        <>
          <AnimationWrapper
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}>
            <GatsbyImage image={pinkSpray} alt="Pink spray" aria-hidden="true" />
          </AnimationWrapper>
          <AnimationWrapper
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}>
            <GatsbyImage image={mesh} alt="Turning hexagonal mesh with multi-coloured points" aria-hidden="true" />
          </AnimationWrapper>
          <AnimationWrapper
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}>
            <GatsbyImage image={threeBalls} alt="Three geometric ball shapes joined by a line" aria-hidden="true" />
          </AnimationWrapper>
          <AnimationWrapper
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}>
            <GatsbyImage image={blueAndBlackSpray.childImageSharp.gatsbyImageData} alt="Blue and black spray" aria-hidden="true" />
          </AnimationWrapper>
        </>
      )}
      <AgencyXLogo style={{ maxWidth: '242px' }} aria-label="Agency X" />
      <p>Start your Omnichannel journey with us</p>
      <button type="button" aria-label="Contact Us">
        <Link to="/contact" id="contact-us-omnichannel">Contact Us</Link>
      </button>
    </Container>
  );
};

export const AnimationWrapper = styled(motion.div)`
  @keyframes pulse {
    0% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    50% {
      transform: rotate(25deg);
    }
    100% {
      transform: rotate(0deg);
    }
  }
`;

const Container = styled.div`
  align-items: center;
  background: rgba(255, 255, 255, 0.6);
  display: flex;
  flex-direction: column;
  gap: 1.325rem;
  height: auto;
  padding: 2.5rem 2rem;
  position: relative;
  text-align: center;
  transition: 0.75s;
  width: 100%;

  @media (min-width: 1150px) {
    padding: 2.5rem 3rem;
  }

  ${AnimationWrapper} {
    position: absolute;
    z-index: 1;
    transition-duration: 0.75s;
    transition-delay: 0.75s;
    :nth-child(1) {
      top: -90px;
      right: 40px;
      width: 207px;
      img {
        animation: pulse 2s ease-in-out infinite alternate-reverse;
      }
    }
    :nth-child(2) {
      top: -70px;
      right: -60px;
      width: 198px;
      img {
        animation: spin 3s ease-in-out infinite;
      }
    }
    :nth-child(3) {
      bottom: -25px;
      left: -16px;
      width: 153px;
    }
    :nth-child(4) {
      bottom: -65px;
      left: -90px;
      width: 220px;
      img {
        animation: pulse 2s 1s ease-in-out infinite alternate-reverse;
      }
    }
  }
  h2 {
    margin-bottom: 1rem;
    text-transform: uppercase;
    color: var(--ax-gold);
  }
  p {
    font-size: 1.5rem;
    line-height: 1.25em;
    color: black;
    font-weight: 300;
    max-width: 800px;
    @media (min-width: 1150px) {
      font-size: 2rem;
      line-height: 1.5em;
    }
  }
  button {
    width: 166px;
    height: 46px;
    font-size: 16px;
    line-height: 19px;
    background: var(--ax-gold);
    color: white;
    text-transform: uppercase;
    font-weight: 700;
    margin-bottom: 0;
    transition: 0.375s;
    cursor: pointer;
    &:hover,
    &:focus {
      border: none;
      outline: none;
      background: var(--ax-black);
    }
    a {
      color: white;
      height: 100%;
      width: 100%;
      text-decoration: none;
    }
  }
`;

export default StartYourJourney;
